import { inject, observer } from 'mobx-react';
import React from 'react';
import { Grid, GridColumn, Separator, Skeleton, SkeletonItem, TabCordion, TabsItem } from 'telia-front-react';
import RouteStore from '../../stores/RouteStore';
import SearchStore, { EMPLOYEE_CATEGORY, ESHOP_CATEGORY, NEWS_CATEGORY, ONLINE_HELP_CATEGORY, SELFSERVICE_CATEGORY } from '../../stores/SearchStore';
import { TranslationsContext } from '@teliaee/telia-translate';
import ContentResults from './ContentResults';
import DealerSearchResults from './DealerSearchResults';
import EShopSearchResults from './EShopSearchResults';
import NewsSearchResults from './NewsSearchResults';
import OnlineHelpSearchResults from './OnlineHelpSearchResults';
import SearchNotFoundResults from '../SearchNotFoundResults';
import SearchResultsSortBar from '../SearchResultsSortBar';
import SelfServiceSearchResults from './SelfServiceSearchResults';
import { MediaQueryWrapper } from '@teliaee/telia-common-components';

export interface SearchResultsProps {
  searchStore?: SearchStore;
  routeStore?: RouteStore;
  isMobile?: boolean;
  recommendedSubPages?: JSX.Element;
}

@observer
class SearchResults extends React.Component<SearchResultsProps> {
  static contextType = TranslationsContext;
  declare context: React.ContextType<typeof TranslationsContext>;

  public render() {
    const translate = this.context.translate;
    const {
      searchStore,
      searchStore: { isAnyCategoryFetching, searchCategories, changeCategories, selectedTabs },
      isMobile
    } = this.props;

    if (isAnyCategoryFetching) {
      return this.renderSkeleton();
    }
    return searchCategories?.length ? (
      <>
        <Separator spacing="small" color="invisible" />
        <TabCordion
          currentTab={selectedTabs}
          border={'top'}
          align={'center'}
          fullWidth={true}
          breakPoint="md"
          onTabChange={(_prevTabs, nextTabs) => changeCategories(nextTabs)}
          defaultCurrentTab={searchCategories[0]}
          aria-labelledby="searchHeading"
        >
          {(searchCategories || []).map((category) => (
            <TabsItem
              id={category}
              label={translate(`search.${category}`)}
              tagContent={searchStore.getSearchResultCount(category, false) || null}
              key={category}
              type={category === EMPLOYEE_CATEGORY ? 'employee' : undefined}
            >
              <Grid align={['center-xs']}>
                <GridColumn>
                  {!!this.props.searchStore.subPageCategories?.length && category === ESHOP_CATEGORY && this.props.recommendedSubPages}
                  <SearchResultsSortBar category={category} isMobile={isMobile} />
                  {this.renderCategory(category)}
                </GridColumn>
              </Grid>
            </TabsItem>
          ))}
        </TabCordion>
      </>
    ) : (
      <SearchNotFoundResults onlyShopSearch={false} isMobile={isMobile} />
    );
  }

  private renderCategory(category: string): JSX.Element {
    switch (category) {
      case EMPLOYEE_CATEGORY:
        return <DealerSearchResults />;
      case NEWS_CATEGORY:
        return <NewsSearchResults />;
      case ESHOP_CATEGORY:
        return <EShopSearchResults />;
      case ONLINE_HELP_CATEGORY:
        return <OnlineHelpSearchResults />;
      case SELFSERVICE_CATEGORY:
        return <SelfServiceSearchResults />;
      default:
        return <ContentResults />;
    }
  }

  private renderSkeleton(): JSX.Element {
    const translate = this.context.translate;
    return (
      <Skeleton label={translate('skeleton.loading')} className="text-center">
        <MediaQueryWrapper
          minWidth="md"
          matchingContent={
            <>
              <Separator spacing="medium" color="invisible" />
              <SkeletonItem width="75%" height="26px" />
              <Separator spacing="xsmall" />
              <Separator spacing="small" color="invisible" />
              <SkeletonItem width="75%" height="480px" />
            </>
          }
          unmatchingContent={
            <>
              <Separator spacing="small" color="invisible" />
              <SkeletonItem width="100%" height="580px" />
            </>
          }
        />
      </Skeleton>
    );
  }
}

export default inject('searchStore')(SearchResults);
